import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  type ResolvedTheme,
  ThemeContext,
  type ThemeContextValue,
  type ThemeMode,
} from '@/hooks/useTheme';

/** The key the product uses, so the two read the same preference per origin. */
const THEME_STORAGE_KEY = 'routly-theme';

function readStoredTheme(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private mode, or storage blocked. The default below is the answer.
  }
  return 'dark';
}

/** `index.html` ships `class="dark"`, so the first paint is never unthemed. */
function applyThemeClass(theme: ResolvedTheme) {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>(readStoredTheme);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>('dark');
  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemTheme(media.matches ? 'dark' : 'light');
    handler();
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    applyThemeClass(resolvedTheme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // A preference that cannot be stored still applies to this tab.
    }
  }, [resolvedTheme, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
